import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
} from 'chart.js';
import { useEffect, useState, useMemo } from 'react';
import { Doughnut, Bar } from 'react-chartjs-2';

import { useAppSelector } from '../redux/store/hooks';
import { useFetchReportsQuery } from '../services/dataReviewerApi/dataReviewerApi';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
);

const chartColors = [
  '#FF6384',
  '#36A2EB',
  '#FFCE56',
  '#4BC0C0',
  '#9966FF',
  '#FF9F40'
];

const ReportsPage = () => {
  const { allReports } = useAppSelector(state => state.root.dataReviewer);
  const { isLoading, error } = useFetchReportsQuery({});
  const [selectedDomain, setSelectedDomain] = useState<string>('');

  useEffect(() => {
    if (
      Object.keys(allReports.questionsbyquestiondomain).length > 0 &&
      !selectedDomain
    ) {
      setSelectedDomain(Object.keys(allReports.questionsbyquestiondomain)[0]);
    }
  }, [allReports]);

  const {
    domainDoughnutData,
    sourceDoughnutData,
    statusDoughnutData,
    barData
  } = useMemo(() => {
    const createDoughnutData = (data: Record<string, number>) => ({
      labels: Object.keys(data),
      datasets: [
        {
          data: Object.values(data),
          backgroundColor: chartColors
        }
      ]
    });

    const subdomains =
      allReports.questionsbyquestiondomainandsubdomain[selectedDomain] || {};

    return {
      domainDoughnutData: createDoughnutData(
        allReports.questionsbyquestiondomain
      ),
      sourceDoughnutData: createDoughnutData(
        allReports.questionsbyquestionsource
      ),
      statusDoughnutData: createDoughnutData(
        allReports.questionsbyquestionstatus
      ),
      barData: {
        labels: Object.keys(subdomains),
        datasets: [
          {
            label: `Subdomains in ${selectedDomain}`,
            data: Object.values(subdomains),
            backgroundColor: '#36A2EB'
          }
        ]
      }
    };
  }, [allReports, selectedDomain]);

  if (isLoading) {
    return (
      <div className='flex h-full items-center justify-center'>Loading...</div>
    );
  }

  if (error) {
    return (
      <div className='flex h-full items-center justify-center text-red-500'>
        Error loading reports
      </div>
    );
  }

  return (
    <div className='mt-8 flex h-full flex-col items-center space-y-8 overflow-y-auto px-6'>
      <div className='flex w-full flex-wrap items-center justify-center gap-4 lg:flex-nowrap'>
        <div className='flex w-1/2 flex-col items-center justify-center'>
          <h2 className='mb-4 text-center text-xl font-semibold'>
            Question Distribution by Status
          </h2>
          <div className='flex w-full justify-center lg:w-1/2 xl:w-2/3'>
            <Doughnut data={statusDoughnutData} />
          </div>
        </div>

        <div className='flex w-1/2 flex-col items-center justify-center'>
          <h2 className='mb-4 text-center text-xl font-semibold'>
            Question Distribution by Source
          </h2>
          <div className='flex w-full justify-center lg:w-1/2 xl:w-2/3'>
            <Doughnut data={sourceDoughnutData} />
          </div>
        </div>
      </div>

      <div className='flex w-full flex-col items-center justify-center'>
        <h2 className='mb-4 text-center text-xl font-semibold'>
          Question Distribution by Domain
        </h2>
        <div className='flex w-1/2 justify-center lg:w-2/3 xl:w-2/5'>
          <Doughnut data={domainDoughnutData} />
        </div>
      </div>

      <div className='w-full'>
        <div className='mb-4 flex items-center justify-center gap-2'>
          <label className='font-medium'>Select Domain:</label>
          <select
            className='rounded-md border p-2'
            value={selectedDomain}
            onChange={e => setSelectedDomain(e.target.value)}
          >
            {Object.keys(allReports.questionsbyquestiondomain).map(domain => (
              <option key={domain} value={domain}>
                {domain}
              </option>
            ))}
          </select>
        </div>

        <div className='mx-auto w-11/12 pb-10 lg:w-[70%]'>
          <h2 className='mb-4 text-center text-xl font-semibold'>
            Subdomain Breakdown - {selectedDomain}
          </h2>
          {barData.labels.length > 0 ? (
            <Bar data={barData} />
          ) : (
            <p className='text-center text-gray-500'>
              No subdomains available for this domain
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
